import {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import axios from "../config/axios-config";
import {useUser} from "../providers/user-context";

function notificationDistributor(rows, ownerId, ownerName) {
    if (ownerId) return rows.some(row => row.distributorId === ownerId) ? {distributorId: ownerId} : null;
    // Older notifications have only the English sender name. Match only authorized order suppliers, without guessing between namesakes.
    const name = (ownerName || "").trim().toLowerCase();
    const ids = new Set(rows.filter(row => name && row.distributorNameEn?.trim().toLowerCase() === name)
        .map(row => row.distributorId).filter(Boolean));
    return ids.size === 1 ? {distributorId: ids.values().next().value} : null;
}

export default function useNotificationOrder(onOpen, onReset) {
    const location = useLocation(), navigate = useNavigate(), {i18n} = useTranslation(), user = useUser();
    const callbacks = useRef({onOpen, onReset});
    callbacks.current = {onOpen, onReset};
    const [state, setState] = useState({loading: false, error: ""}), [retry, setRetry] = useState(0);
    const target = location.state?.notificationOrder;
    const number = target?.orderNumber, ownerId = target?.ownerId, ownerName = target?.ownerName;
    const scope = [location.key, user.tenantId, user.userFullName, user.tenantType, user.userRole, i18n.language].join(":");
    useEffect(() => {
        if (!number || !user.tenantId || user.userLoading || user.userError) return;
        const controller = new AbortController(), pharmacy = user.tenantType === "PHARMACY";
        callbacks.current.onReset();
        setState({scope, loading: true, error: ""});
        // Fetch current, authorized data before handing off to the normal Orders dialog.
        axios.get(pharmacy ? "/api/v1/orders/pharmacy/common-details" : `/api/v1/orders/importer/${encodeURIComponent(number)}`, {
            params: pharmacy ? {orderNumber: number} : undefined, signal: controller.signal,
            headers: {"Language-Context": i18n.language.toUpperCase()},
        }).then(({data}) => {
            if (controller.signal.aborted) return;
            if (!data || (pharmacy && !data.length)) throw new Error("Order unavailable");
            callbacks.current.onOpen(pharmacy ? {orderNumber: number, distributor: notificationDistributor(data, ownerId, ownerName)} : data);
            setState({scope, loading: false, error: ""});
            // Consume the hint so a dismissed dialog does not reopen on a later language change.
            navigate(location.pathname + location.search + location.hash, {replace: true,
                state: {...location.state, notificationOrder: null}});
        }).catch(error => {
            if (!controller.signal.aborted) setState({scope, loading: false,
                error: error.response?.data?.message || "NotificationOrderUnavailable"});
        });
        return () => controller.abort();
    }, [number, ownerId, ownerName, scope, user.tenantId, user.tenantType, user.userLoading, user.userError, i18n.language, retry,
        navigate, location.pathname, location.search, location.hash, location.state]);
    return {...(state.scope === scope ? state : {loading: false, error: ""}), retry: () => setRetry(value => value + 1)};
}
